/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2023, Rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of Rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by Rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/
/* eslint @typescript-eslint/no-explicit-any: 0 */

import { useState, useEffect } from "react";
import { useAsyncCallback } from "react-async-hook";
import type { AsyncStateStatus } from "react-async-hook";
import { useAtom, PrimitiveAtom } from "jotai";
import { isEmpty, isEqualWith } from "lodash-es";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { EventHandler } from "@rfice/eventhandler";
import { _T } from "@rfice/language";

export default function makeAPI<Response = UnknownResult, Arguments extends any[] = UnknownArguments>(executor: {
	name: string;
	events: API.Events;
	callback: (...args: Arguments) => MaybePromise<Response>;
	cache: PrimitiveAtom<Response>;
}): API.Result<Response> {
	const { name, events, callback, cache } = executor;
	const api = useAsyncCallback<Response, Arguments>(callback);
	const [storeCache, setStoreCache] = useAtom(cache);
	const [APIStatus, setAPIStatus] = useState<AsyncStateStatus>("not-requested");

	useEffect(() => {
		if (api.status === "success" && api.result) {
			events?.onSuccess?.(name, api.result);
			EventHandler.emit(
				`API-${name}-Okay`,
				Object.assign(
					{
						// @ts-ignore
						code: api.result.code,
						// @ts-ignore
						data: api.result.data,
						// @ts-ignore
						parameter: api.currentParams
					},
					{ currentParams: api.currentParams }
				)
			);
			setAPIStatus(api.status);
		}
	}, [storeCache]);

	useEffect(() => {
		if (api.status === "success" && api.result) {
			// @ts-ignore
			setStoreCache({
				// @ts-ignore
				code: api.result.code,
				// @ts-ignore
				data: api.result.data,
				// @ts-ignore
				parameter: api.currentParams
			});
			return;
		}
		if (api.loading) {
			events?.onLoading?.(name);
			EventHandler.emit(`API-${name}-Loading`);
		}
		if (api.status === "error") {
			const apiError = api.error as RestAPIError;
			events?.onError?.(name, apiError);
			EventHandler.emit(`API-${name}-Error`, apiError);
		}
		setAPIStatus(api.status);
	}, [api.status]);

	return {
		name,
		get execute() {
			return api.execute;
		},
		get status() {
			return APIStatus;
		},
		get error() {
			return api.error;
		},
		get result() {
			return storeCache;
		},
		get isSuccess() {
			return this.status === "success";
		},
		get isLoading() {
			return this.status === "loading";
		},
		get hasError() {
			return this.status === "error";
		}
	};
}
